import { Link } from 'react-router-dom';

/**
 * Shared internal-navigation link component (link-standard-navigation-
 * behavior-trd.md Section 2.1, R1). Thin wrapper around React Router's
 * <Link> that every internal navigation click target should render through,
 * so standard anchor-tag click semantics -- left-click = SPA navigation,
 * right-click = native context menu, ctrl/cmd/middle-click = new background
 * tab, Tab+Enter = reachable/activatable -- hold by construction instead of
 * depending on every call site remembering not to break them.
 *
 * Centralized here (rather than using <Link> directly at every call site)
 * because several existing consumers compose navigation with a secondary
 * side effect on the same click (e.g. Sidebar.jsx closing a mobile drawer
 * after navigating via `onNavigate`).
 *
 * @param {string} [to] - route path (typically built via buildProjectPath).
 *   If falsy, a plain non-interactive <span> is rendered instead of a link
 *   (defensive -- should not occur at any current call site, all of which
 *   have a resolvable destination).
 * @param {(event: React.MouseEvent) => void} [onNavigate] - optional side
 *   effect fired alongside navigation on click. Deliberately does NOT call
 *   event.preventDefault() -- doing so would suppress <Link>'s own
 *   modifier-key/left-click-only interception logic, which already
 *   correctly leaves ctrl/cmd/middle-click and right-click un-intercepted.
 */
const AppLink = ({ to, onNavigate, className, children, ...rest }) => {
  if (!to) {
    return (
      <span className={className} {...rest}>
        {children}
      </span>
    );
  }

  const handleClick = (event) => {
    if (onNavigate) onNavigate(event);
  };

  return (
    <Link to={to} className={className} onClick={handleClick} {...rest}>
      {children}
    </Link>
  );
};

export default AppLink;
